import { css, cva } from '@styled-system/css';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';

import type { BillingPeriod } from '@/features/subscription/types';

interface BillingPeriodToggleProps {
  value: BillingPeriod;
  onChange: (period: BillingPeriod) => void;
  /** Texte du badge à côté du toggle Annuel (ex: "−15%"). null pour cacher. */
  discount?: string | null;
  disabled?: boolean;
}

const wrap = css({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '3',
});

const pill = css({
  display: 'inline-flex',
  padding: '1',
  borderRadius: 'full',
  bg: 'surface.sunken',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'border.subtle',
});

const segment = cva({
  base: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    paddingX: '5',
    paddingY: '2',
    borderRadius: 'full',
    fontFamily: 'sans',
    fontSize: 'bodySm',
    fontWeight: 'medium',
    bg: 'transparent',
    border: 'none',
    cursor: 'pointer',
    transition: 'background 120ms ease, color 120ms ease, box-shadow 120ms ease',
    _disabled: { cursor: 'not-allowed', opacity: 0.5 },
  },
  variants: {
    active: {
      true: {
        bg: 'surface.elevated',
        color: 'text.primary',
        boxShadow: 'xs',
      },
      false: {
        color: 'text.secondary',
        _hover: { color: 'text.primary' },
      },
    },
  },
});

const discountBadge = css({
  display: 'inline-flex',
  alignItems: 'center',
  paddingX: '2',
  paddingY: '0.5',
  borderRadius: 'full',
  bg: 'success.soft',
  color: 'success.default',
  fontFamily: 'sans',
  fontSize: 'caption',
  fontWeight: 'semibold',
  letterSpacing: '0.2px',
});

export const BillingPeriodToggle: FC<BillingPeriodToggleProps> = ({
  value,
  onChange,
  discount = '−15%',
  disabled = false,
}) => {
  const { t } = useTranslation();

  return (
    <div className={wrap}>
      <div className={pill} role="tablist" aria-label={t('subscriptionPage.billingPeriodAria')}>
        <button
          type="button"
          role="tab"
          aria-selected={value === 'monthly'}
          disabled={disabled}
          className={segment({ active: value === 'monthly' })}
          onClick={() => onChange('monthly')}
        >
          {t('subscriptionPage.billingMonthly')}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={value === 'yearly'}
          disabled={disabled}
          className={segment({ active: value === 'yearly' })}
          onClick={() => onChange('yearly')}
        >
          {t('subscriptionPage.billingYearly')}
        </button>
      </div>
      {discount && <span className={discountBadge}>{discount}</span>}
    </div>
  );
};
